import { createContext, useContext, useEffect } from "react";

export const HeadMetadataContext = createContext(null);

const selectorFor = entry => {
  if (entry.tag === "title") return "title";
  const attrs = entry.attrs;
  if (entry.tag === "meta") {
    const key = ["name", "property", "http-equiv"].find(name => attrs[name]);
    return `meta[${key}="${attrs[key]}"]`;
  }
  return `link[rel="${attrs.rel}"]${attrs.hreflang ? `[hreflang="${attrs.hreflang}"]` : ""}`;
};

// One explicit owner for server and client head metadata. Native React hoisting
// cannot adopt nodes manually relocated outside a div hydration root.
export const useHeadMetadata = entries => {
  const context = useContext(HeadMetadataContext);
  if (context) context.entries = entries;
  const serialized = JSON.stringify(entries);
  useEffect(() => {
    JSON.parse(serialized).forEach(entry => {
      const matches = [...document.head.querySelectorAll(selectorFor(entry))];
      const element = matches.shift() || document.createElement(entry.tag);
      matches.forEach(duplicate => duplicate.remove());
      element.setAttribute("data-ozc-seo", entry.key);
      Object.entries(entry.attrs || {}).forEach(([key, value]) => element.setAttribute(key, value));
      if (entry.tag === "title") element.textContent = entry.text;
      if (!element.isConnected) document.head.appendChild(element);
    });
  }, [serialized]);
};

const escape = value => String(value).replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
export const serializeHeadMetadata = entries => entries.map(entry => {
  const attrs = Object.entries({ "data-ozc-seo": entry.key, ...entry.attrs }).map(([key, value]) => `${key}="${escape(value)}"`).join(" ");
  return entry.tag === "title" ? `<title ${attrs}>${escape(entry.text)}</title>` : `<${entry.tag} ${attrs}>`;
}).join("\n");